import { computed, type ComputedRef } from 'vue'

import type { Domain } from '@gro-up/types'

export type { Domain }

export const domainBackgroundClasses: Record<Domain, string> = {
  'maternity-care': 'bg-maternity-care-100 hover:bg-maternity-care-90',
  'child-care': 'bg-child-care-100 hover:bg-child-care-90',
  'community-work': 'bg-community-work-100 hover:bg-community-work-90',
  'youth-support': 'bg-youth-support-100 hover:bg-youth-support-90',
  'head-office': 'bg-primary-100 hover:bg-primary-90',
};

// Non-hover base background classes (strictly the darkest/base tone)
export const domainBaseBackgroundClasses: Record<Domain, string> = {
  'maternity-care': 'bg-maternity-care-100',
  'child-care': 'bg-child-care-100',
  'community-work': 'bg-community-work-100',
  'youth-support': 'bg-youth-support-100',
  'head-office': 'bg-primary-100',
};

export const domainIllustrationClasses: Record<Domain, string> = {
  'maternity-care': 'shape-maternity-care',
  'child-care': 'shape-child-care',
  'community-work': 'shape-community-work',
  'youth-support': 'shape-youth-support',
  'head-office': 'shape-head-office',
};

// CSS variable values for darker (90) shade per domain
export const domainDarkerBackgroundVars: Record<Domain, string> = {
  'maternity-care': 'var(--color-maternity-care-90)',
  'child-care': 'var(--color-child-care-90)',
  'community-work': 'var(--color-community-work-90)',
  'youth-support': 'var(--color-youth-support-90)',
  'head-office': 'var(--color-primary-90)',
};

// CSS variable values for base (100) shade per domain
export const domainBaseBackgroundVars: Record<Domain, string> = {
  'maternity-care': 'var(--color-maternity-care-100)',
  'child-care': 'var(--color-child-care-100)',
  'community-work': 'var(--color-community-work-100)',
  'youth-support': 'var(--color-youth-support-100)',
  'head-office': 'var(--color-primary-100)',
};

export const domainLabels: Record<Domain, string> = {
  "maternity-care": "kraamzorg",
  "child-care": "kinderopvang",
  "youth-support": "jeugdhulp",
  "community-work": "buurtwerk",
  "head-office": "hoofdkantoor",
};

export const useDomainMapper = () => {
  /**
   * Map GroUp domains to human-readable labels
   */
  const getDomainLabel = (domain: Domain | string | null | undefined): string => {
    if (!domain) return ''
    return domainLabels[domain as Domain] ?? domain
  }

  /**
   * Map GroUp domains to Tailwind background color classes
   */
  const getDomainBackgroundClass = (domain?: Domain | string): string => {
    if (!domain) {
      return 'bg-primary-100' // default color if domain is not provided
    }
    return domainBackgroundClasses[domain as Domain] || 'bg-primary-100'
  }

  /**
   * Map GroUp domains to the base (non-hover) background class only
   */
  const getDomainBaseBackgroundClass = (domain?: Domain | string): string => {
    if (!domain) {
      return 'bg-primary-100'
    }
    return domainBaseBackgroundClasses[domain as Domain] || 'bg-primary-100'
  }

  const getDomainIllustrationClass = (domain?: Domain | string): string => {
    if (!domain) {
      return 'shape-rounded'
    }
    return domainIllustrationClasses[domain as Domain] || 'shape-rounded'
  }

  // Return CSS variable string for darker (90) shade
  const getDomainDarkerBackgroundVar = (domain?: Domain | string): string => {
    if (!domain) {
      return 'var(--color-primary-90)'
    }
    return domainDarkerBackgroundVars[domain as Domain] || 'var(--color-primary-90)'
  }

  // Return CSS variable string for base (100) shade
  const getDomainBaseBackgroundVar = (domain?: Domain | string): string => {
    if (!domain) {
      return 'var(--color-primary-100)'
    }
    return domainBaseBackgroundVars[domain as Domain] || 'var(--color-primary-100)'
  }

  return {
    getDomainLabel,
    getDomainBackgroundClass,
    getDomainBaseBackgroundClass,
    getDomainIllustrationClass,
    getDomainDarkerBackgroundVar,
    getDomainBaseBackgroundVar,
  }
}

